import React, { useEffect, useId, useState } from "react";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import ProductDetailGallery from "./ProductDetailGallery";
import ProductDetailReviews from "./ProductDetailReviews";
import { getOriginalPrice, getProductPortions, getProductPrice, getProductUnit, hasPrice } from "../../utils/productOptions";
import "./product-detail.css";

export default function ProductDetailSection({ product, cartItem, onAddToCart, onQuantityChange, onPortionChange, adding = false }) {
  const portionId = useId();
  const portions = getProductPortions(product);
  const confirmedUnit = portions.find((item) => item.portion === cartItem?.portion)?.portion || "";
  const [selectedUnit, setSelectedUnit] = useState(confirmedUnit || portions[0]?.portion || "");
  const portion = portions.find((item) => item.portion === (confirmedUnit || selectedUnit)) || portions[0];
  const samePortion = !portions.length || cartItem?.portion === portion?.portion;
  const quantity = samePortion ? Math.max(0, Number(cartItem?.quantity) || 0) : 0;
  useEffect(() => {
    if (confirmedUnit) setSelectedUnit(confirmedUnit);
  }, [confirmedUnit]);

  const selectPortion = async (unit) => {
    if (adding) return;
    const next = portions.find((item) => item.portion === unit);
    if (!next) return;
    if (cartItem && onPortionChange && !await onPortionChange(next)) return;
    setSelectedUnit(unit);
  };
  const title = product.title || product.name || product.product_name || "Product details";
  const price = Number(getProductPrice(product, portion)) || 0;
  const originalPrice = getOriginalPrice(product, portion);
  const currency = product.currency || product.currency_code || "INR";
  const discount = Number(originalPrice) > price ? Math.round((1 - price / Number(originalPrice)) * 100) : 0;
  const description = product.description || product.product_description || "No description available for this product.";
  const attributes = [
    ["Brand", product.brand?.name || product.brand_name || product.brand],
    ["Manufacturer", product.manufacturer?.name || product.manufacturer_name || product.manufacturer],
    ["Country of Origin", product.country_of_origin || product.origin],
  ];

  return (
    <section className="productdetail-section" aria-labelledby="productdetail-title">
      <div className="container container-lg">
        <div className="productdetail-layout">
          <ProductDetailGallery product={product} title={title} discount={discount} />
          <div className="productdetail-content">
            <h1 id="productdetail-title">{title}</h1>
            {(product.category_name || product.subtitle) && <p className="productdetail-content__category">{product.category_name || product.subtitle}</p>}
            <div className="productdetail-content__unit">
              {portions.length > 1 ? <>
                <label className="visually-hidden" htmlFor={portionId}>Select weight</label>
                <select id={portionId} value={portion?.portion || ""} disabled={adding} onChange={(event) => selectPortion(event.target.value)}>
                  {portions.map((item) => <option key={item.portion} value={item.portion}>{item.portion}</option>)}
                </select>
              </> : <span>{portion?.portion || getProductUnit(product)}</span>}
            </div>
            <div className="productdetail-content__price" aria-live="polite" aria-atomic="true">
              <span>{currency} {price.toFixed(2)}</span>
              {hasPrice(originalPrice) && <del>{currency} {Number(originalPrice).toFixed(2)}</del>}
            </div>
            {quantity > 0 ? (
              <div className="productdetail-quantity" role="group" aria-label={`Quantity for ${title}`} aria-busy={adding}>
                <button type="button" disabled={adding} aria-label={`Decrease ${title} quantity`} onClick={() => onQuantityChange?.(quantity - 1)}><Minus size={18} aria-hidden="true" /></button>
                <output className="productdetail-quantity__value" aria-label="Quantity" aria-live="polite" aria-atomic="true">{quantity}</output>
                <button type="button" disabled={adding} aria-label={`Increase ${title} quantity`} onClick={() => onQuantityChange?.(quantity + 1)}><Plus size={18} aria-hidden="true" /></button>
              </div>
            ) : <button type="button" className="productdetail-content__add" disabled={adding} onClick={() => onAddToCart?.({ product, portion: portion?.portion || null, price, quantity: 1 })}>
              <ShoppingCart size={17} aria-hidden="true" /> {adding ? "Adding..." : "Add to Cart"}
            </button>}
            <div className="productdetail-description">
              <h2>Description</h2>
              <p>{description}</p>
            </div>
            <dl className="productdetail-attributes">
              {attributes.map(([label, content]) => <div key={label}><dt>{label}</dt><dd>{typeof content === "string" || typeof content === "number" ? content : "—"}</dd></div>)}
            </dl>
            <ProductDetailReviews summary={product.review_summary || undefined} />
          </div>
        </div>
      </div>
    </section>
  );
}
